import React, { useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Swal from "sweetalert2";
import { IMPORT_ACTIONS } from "../gestionCarteraApi";
import { COLORS, ModalShell, SecondaryButton } from "../gestionCarteraUi";

const APPLIER_FILENAME = "aplicador_pagos.xlsx";

const SECTIONS = [
  {
    title: "Pagos",
    actions: [
      { key: "payQuotas", label: "Pagar cuotas", success: "Las cuotas se pagaron correctamente." },
      { key: "deleteQuotas", label: "Eliminar cuotas", success: "Las cuotas se eliminaron correctamente." },
      { key: "deletePaymentRecord", label: "Eliminar recaudo", success: "El recaudo se eliminó correctamente." },
    ],
  },
  {
    title: "Gestión",
    actions: [
      { key: "registerCollection", label: "Registrar cobranza", success: "La gestión de cobranza se importó correctamente." },
      { key: "commitmentDate", label: "Fecha de compromiso", success: "Las fechas de compromiso se actualizaron correctamente." },
    ],
  },
  {
    title: "Aplicación",
    actions: [
      { key: "paymentApplier", label: "Generar aplicador de pagos", success: "El aplicador de pagos se generó y se descargó." },
      { key: "applicationAdjustment", label: "Ajuste de aplicación", success: "El ajuste de aplicación se procesó correctamente." },
    ],
  },
];

const getFilename = (response) => {
  const disposition = response.headers?.["content-disposition"];
  const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  return match ? match[1].replace(/['"]/g, "") : APPLIER_FILENAME;
};

const downloadBlob = (response) => {
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFilename(response);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Ocurrió un error al procesar el archivo.";

/** M1 · Importar: acciones CSV del antiguo módulo Cobranza. */
export default function ImportarModal({ open, onClose }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [runningAction, setRunningAction] = useState(null);

  const handleClose = () => {
    if (runningAction) return;
    setFile(null);
    onClose();
  };

  const handleRun = async (action) => {
    setRunningAction(action.key);
    try {
      const response = await IMPORT_ACTIONS[action.key](file);
      if (action.key === "paymentApplier") downloadBlob(response);
      setRunningAction(null);
      handleClose();
      Swal.fire({ icon: "success", title: action.label, text: action.success, confirmButtonColor: COLORS.primary });
    } catch (error) {
      setRunningAction(null);
      Swal.fire({ icon: "error", title: "Error", text: getErrorMessage(error), confirmButtonColor: COLORS.primary });
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const dropped = event.dataTransfer.files?.[0];
    if (dropped?.name?.toLowerCase().endsWith(".csv")) setFile(dropped);
  };

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title="Importar"
      subtitle="Carga un archivo CSV para habilitar las acciones."
      maxWidth="md"
      actions={<SecondaryButton onClick={handleClose}>Cancelar</SecondaryButton>}
    >
      <Box
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed #D0D5DD",
          borderRadius: "14px",
          backgroundColor: COLORS.rowHover,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          textAlign: "center",
        }}
      >
        <UploadFileIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
        <Typography sx={{ fontWeight: 500 }}>
          {file ? file.name : "Arrastra y suelta tu archivo CSV aquí"}
        </Typography>
        <Typography sx={{ color: COLORS.muted }}>o</Typography>
        <SecondaryButton onClick={() => fileInput.current?.click()}>
          {file ? "Cambiar archivo" : "Seleccionar archivo"}
        </SecondaryButton>
        <input
          ref={fileInput}
          type="file"
          accept=".csv"
          hidden
          onChange={(event) => setFile(event.target.files?.[0] || null)}
        />
        <Typography sx={{ fontSize: 14, color: COLORS.muted }}>Solo archivos .csv</Typography>
      </Box>

      {SECTIONS.map((section) => (
        <Box key={section.title}>
          <Typography
            component="h3"
            sx={{ fontSize: 14, fontWeight: 700, color: COLORS.muted, mb: 1.5 }}
          >
            {section.title}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {section.actions.map((action) => (
              <SecondaryButton
                key={action.key}
                disabled={!file || Boolean(runningAction)}
                onClick={() => handleRun(action)}
                startIcon={runningAction === action.key ? <CircularProgress size={16} /> : null}
              >
                {action.label}
              </SecondaryButton>
            ))}
          </Box>
        </Box>
      ))}
    </ModalShell>
  );
}
